import { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import DatePicker from "react-datepicker";
import {
  Badge,
  Form,
  FormGroup,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import { BiEdit } from "react-icons/bi";
import { BsFiletypeDocx } from "react-icons/bs";
import { FaFilePdf } from "react-icons/fa";
import Select from "react-select";
import { toast } from "react-toastify";

import {
  addNewsAPI,
  editNewsAPI,
  getNewsAPI,
} from "../../api/banner";
import { updateNewsStatusAPI } from "../../api/menu";

import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Button,
  Col,
  Icon,
  PreviewAltCard,
  Row,
} from "../../components/Component";

import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import useLogout from "../../hooks/useLogout";

import MyDataTable from "../../myComponents/DataTable/MyDataTable";
import ConfirmModal from "../../myComponents/modals/ConfirmModal";
import ViewImgAndPdf from "../../myComponents/modals/ViewImgAndPdf";

import {
  decrypt,
  encrypt,
  encryptFormData,
} from "../../utils/Encrypt";

import {
  ValidateDocFile,
  ValidatePdf,
} from "../../utils/Utils";

const PAGE_TYPE_OPTIONS = [
  {
    value: "News",
    label: "News",
  },
  {
    value: "Events and Announcement",
    label: "Events and Announcement",
  },
];

const DOCUMENT_FORMAT_OPTIONS = [
  {
    value: "pdf",
    label: "PDF",
  },
  {
    value: "docs",
    label: "DOC/DOCX",
  },
];

const STATUS_OPTIONS = [
  {
    value: true,
    label: "Active",
  },
  {
    value: false,
    label: "InActive",
  },
];

const ERROR_STYLE = {
  color: "#e85347",
  fontSize: "11px",
  fontStyle: "italic",
};

const HINDI_TEXT_PATTERN =
  /^[\u0900-\u097F\s\d.,!?"'()\-–—:;/%&।॥]+$/;

const isFile = (value) => {
  return (
    typeof File !== "undefined" &&
    value instanceof File
  );
};

const normalizeBoolean = (value) => {
  return (
    value === true ||
    value === "true" ||
    value === 1 ||
    value === "1"
  );
};

const normalizeDocumentFormat = (value) => {
  const format = String(value || "").toLowerCase();

  if (
    format === "doc" ||
    format === "docx" ||
    format === "docs"
  ) {
    return "docs";
  }

  return "pdf";
};

const formatDateForApi = (date) => {
  if (
    !(date instanceof Date) ||
    Number.isNaN(date.getTime())
  ) {
    return "";
  }

  const year = date.getFullYear();

  const month = String(
    date.getMonth() + 1
  ).padStart(2, "0");

  const day = String(
    date.getDate()
  ).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const parseApiDate = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? null
      : value;
  }

  const dateOnlyMatch = String(value).match(
    /^(\d{4})-(\d{2})-(\d{2})/
  );

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;

    return new Date(
      Number(year),
      Number(month) - 1,
      Number(day)
    );
  }

  const parsedDate = new Date(value);

  return Number.isNaN(parsedDate.getTime())
    ? null
    : parsedDate;
};

const getFileExtension = (filePath = "") => {
  const cleanPath = String(filePath).split(
    /[?#]/
  )[0];

  if (!cleanPath.includes(".")) {
    return "";
  }

  return cleanPath
    .split(".")
    .pop()
    .toLowerCase();
};

const fileMatchesFormat = (
  file,
  format
) => {
  if (!isFile(file)) {
    return false;
  }

  const extension = getFileExtension(
    file.name
  );

  if (format === "pdf") {
    return extension === "pdf";
  }

  if (format === "docs") {
    return (
      extension === "doc" ||
      extension === "docx"
    );
  }

  return false;
};

const News = () => {
  const {
    control,
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      heading: "",
      heading_hindi: "",
      title: "",
      title_hindi: "",
      desc: "",
      desc_hindi: "",
      uploading_date: null,
      page_type: null,
      documentFormate: null,
      documentUpload: null,
      documentUploadHN: null,
      status: null,
    },
  });

  const logout = useLogout();

  const englishFileInputRef =
    useRef(null);

  const hindiFileInputRef =
    useRef(null);

  const [isOpen, setIsOpen] =
    useState(false);

  const [isLoading, setIsLoading] =
    useState(false);

  const [mode, setMode] =
    useState("Add");

  const [editingId, setEditingId] =
    useState(null);

  const [data, setData] =
    useState([]);

  const [
    currentImageIndex,
    setCurrentImageIndex,
  ] = useState(0);

  const [
    modalOpen,
    setModalOpen,
  ] = useState(false);

  const [
    titleName,
    setTitleName,
  ] = useState("");

  const [
    fileType,
    setFileType,
  ] = useState("");

  const [
    modalImages,
    setModalImages,
  ] = useState([]);

  const [
    statusRecord,
    setStatusRecord,
  ] = useState(null);

  const [
    showModal,
    setShowModal,
  ] = useState(false);

  /*
   * These states contain only existing
   * server file paths.
   *
   * File objects are stored inside
   * react-hook-form fields.
   */
  const [
    document,
    setDocument,
  ] = useState(null);

  const [
    documentHN,
    setDocumentHN,
  ] = useState(null);

  const [
    originalDocumentFormat,
    setOriginalDocumentFormat,
  ] = useState("");

  const [
    showUploadField,
    setShowUploadField,
  ] = useState(true);

  const [
    showUploadFieldHN,
    setShowUploadFieldHN,
  ] = useState(true);

  const selectedFormat =
    watch("documentFormate")?.value || "";

  const imageBaseUrl =
    localStorage.getItem(
      "IMAGE_BASE_URL"
    ) || "";

  const buildFileUrl = useCallback(
    (path) => {
      if (!path) {
        return "";
      }

      if (/^https?:\/\//i.test(path)) {
        return path;
      }

      const baseUrl =
        imageBaseUrl.replace(/\/$/, "");

      const filePath = String(
        path
      ).replace(/^\//, "");

      return `${baseUrl}/${filePath}`;
    },
    [imageBaseUrl]
  );

  const clearSelectedFiles =
    useCallback(() => {
      setValue(
        "documentUpload",
        null,
        {
          shouldDirty: false,
          shouldValidate: false,
        }
      );

      setValue(
        "documentUploadHN",
        null,
        {
          shouldDirty: false,
          shouldValidate: false,
        }
      );

      if (
        englishFileInputRef.current
      ) {
        englishFileInputRef.current.value =
          "";
      }

      if (
        hindiFileInputRef.current
      ) {
        hindiFileInputRef.current.value =
          "";
      }

      clearErrors([
        "documentUpload",
        "documentUploadHN",
      ]);
    }, [
      clearErrors,
      setValue,
    ]);

  useEffect(() => {
    /*
     * File inputs are managed manually
     * because their values are File objects.
     */
    register("documentUpload");
    register("documentUploadHN");
  }, []);

  const getNEWSList =
    useCallback(async () => {
      try {
        const res =
          await getNewsAPI({});

        const status = decrypt(
          res.data.status
        );

        if (status === "success") {
          const decryptedResponse =
            decrypt(
              res.data.data.tableData
            );

          setData(
            Array.isArray(
              decryptedResponse
            )
              ? decryptedResponse
              : []
          );
        } else if (
          status === "failed"
        ) {
          setData([]);

          toast.warning(
            res.data.message
          );

          toast.clearWaitingQueue();
        } else if (
          status === "expired"
        ) {
          toast.error(
            res.data.message
          );

          toast.clearWaitingQueue();

          logout();
        }
      } catch (error) {
        console.error(
          "Unable to load news list:",
          error
        );

        toast.error(
          error?.response?.data
            ?.message ||
            error?.message ||
            "Unable to load news data."
        );
      }
    }, [logout]);

  useEffect(() => {
    getNEWSList();
  }, []);

  const resetFormForAdd =
    useCallback(() => {
      reset({
        heading: "",
        heading_hindi: "",
        title: "",
        title_hindi: "",
        desc: "",
        desc_hindi: "",
        uploading_date: null,
        page_type: null,
        documentFormate: null,
        documentUpload: null,
        documentUploadHN: null,
        status: null,
      });

      setEditingId(null);

      setDocument(null);

      setDocumentHN(null);

      setOriginalDocumentFormat(
        ""
      );

      setShowUploadField(true);

      setShowUploadFieldHN(true);

      clearSelectedFiles();
    }, [
      clearSelectedFiles,
      reset,
    ]);

  const handleAddToggle = () => {
    if (
      isOpen &&
     ( mode === "Add"||mode=="Edit")
    ) {
      setIsOpen(false);
      return;
    }

    setMode("Add");

    resetFormForAdd();

    setIsOpen(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleEdit = (row) => {
    const rowFormat =
      normalizeDocumentFormat(
        row?.doc_format
      );

    const existingEnglishDocument =
      row?.document || null;

    const existingHindiDocument =
      row?.hn_document || null;

    setMode("Edit");

    setEditingId(
      row?.id ?? null
    );

    setDocument(
      existingEnglishDocument
    );

    setDocumentHN(
      existingHindiDocument
    );

    setOriginalDocumentFormat(
      rowFormat
    );

    /*
     * If an existing document is found,
     * hide the file input initially.
     *
     * User can click Change to upload
     * a replacement.
     */
    setShowUploadField(
      !existingEnglishDocument
    );

    setShowUploadFieldHN(
      !existingHindiDocument
    );

    reset({
      heading:
        row?.heading || "",

      heading_hindi:
        row?.hn_heading || "",

      title:
        row?.title || "",

      title_hindi:
        row?.hn_title || "",

      desc:
        row?.description || "",

      desc_hindi:
        row?.hn_description || "",

      uploading_date:
        parseApiDate(row?.date),

      page_type: row?.type
        ? {
            label: row.type,
            value: row.type,
          }
        : null,

      documentFormate:
        DOCUMENT_FORMAT_OPTIONS.find(
          (option) =>
            option.value ===
            rowFormat
        ) || null,

      documentUpload: null,

      documentUploadHN: null,

      status: normalizeBoolean(
        row?.status
      )
        ? STATUS_OPTIONS[0]
        : STATUS_OPTIONS[1],
    });

    clearSelectedFiles();

    setIsOpen(true);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleFormatChange = (
    selectedOption,
    onChange
  ) => {
    onChange(selectedOption);

    clearErrors(
      "documentFormate"
    );

    clearSelectedFiles();

    /*
     * Existing files cannot be kept
     * when their file format changes.
     *
     * Reveal both inputs so replacement
     * files can be selected.
     */
    if (
      mode === "Edit" &&
      selectedOption?.value &&
      selectedOption.value !==
        originalDocumentFormat
    ) {
      setShowUploadField(true);

      setShowUploadFieldHN(true);
    }
  };

  const handleFileChange = (
    event,
    fieldName
  ) => {
    const input = event.target;

    const file =
      input.files?.[0] || null;

    if (!file) {
      setValue(
        fieldName,
        null,
        {
          shouldDirty: true,
          shouldValidate: false,
        }
      );

      return;
    }

    if (!selectedFormat) {
      input.value = "";

      setValue(
        fieldName,
        null,
        {
          shouldDirty: true,
        }
      );

      setError(fieldName, {
        type: "manual",
        message:
          "Please select document format first.",
      });

      return;
    }

    const finishValidation = (
      isValid,
      validationMessage
    ) => {
      if (!isValid) {
        input.value = "";

        setValue(
          fieldName,
          null,
          {
            shouldDirty: true,
            shouldValidate: false,
          }
        );

        setError(fieldName, {
          type: "manual",
          message:
            validationMessage ||
            (selectedFormat ===
            "pdf"
              ? "Invalid PDF file."
              : "Invalid file type. Only DOC or DOCX files are allowed."),
        });

        return;
      }

      clearErrors(fieldName);

      setValue(
        fieldName,
        file,
        {
          shouldDirty: true,
          shouldValidate: false,
        }
      );
    };

    if (
      selectedFormat === "pdf"
    ) {
      ValidatePdf(
        file,
        finishValidation
      );

      return;
    }

    ValidateDocFile(
      file,
      (
        isValid,
        message
      ) => {
        finishValidation(
          isValid,
          message ||
            "Invalid file type. Only DOC or DOCX files are allowed."
        );
      }
    );
  };

  const validateDocumentsBeforeSubmit =
    (formValues) => {
      const format =
        formValues
          .documentFormate?.value;

      const englishFile =
        formValues.documentUpload;

      const hindiFile =
        formValues.documentUploadHN;

      const formatChanged =
        mode === "Edit" &&
        originalDocumentFormat &&
        format !==
          originalDocumentFormat;

      let isValid = true;

      /*
       * Files are required:
       *
       * 1. During Add.
       * 2. When the existing file is missing.
       * 3. When document format changes.
       */
      const englishFileRequired =
        mode === "Add" ||
        !document ||
        Boolean(formatChanged);

      const hindiFileRequired =
        mode === "Add" ||
        !documentHN ||
        Boolean(formatChanged);

      if (
        englishFileRequired &&
        !isFile(englishFile)
      ) {
        setError(
          "documentUpload",
          {
            type: "manual",
            message:
              "English document is required.",
          }
        );

        isValid = false;
      }

      if (
        hindiFileRequired &&
        !isFile(hindiFile)
      ) {
        setError(
          "documentUploadHN",
          {
            type: "manual",
            message:
              "Hindi document is required.",
          }
        );

        isValid = false;
      }

      if (
        isFile(englishFile) &&
        !fileMatchesFormat(
          englishFile,
          format
        )
      ) {
        setError(
          "documentUpload",
          {
            type: "manual",
            message:
              `English document must match the selected ${format.toUpperCase()} format.`,
          }
        );

        isValid = false;
      }

      if (
        isFile(hindiFile) &&
        !fileMatchesFormat(
          hindiFile,
          format
        )
      ) {
        setError(
          "documentUploadHN",
          {
            type: "manual",
            message:
              `Hindi document must match the selected ${format.toUpperCase()} format.`,
          }
        );

        isValid = false;
      }

      return isValid;
    };

  const handleApiResponse =
    async (res) => {
      const status = decrypt(
        res.data.status
      );

      if (status === "success") {
        toast.success(
          res.data.message
        );

        toast.clearWaitingQueue();

        setIsOpen(false);

        resetFormForAdd();

        await getNEWSList();

        return;
      }

      if (status === "failed") {
        toast.error(
          res.data.message
        );

        toast.clearWaitingQueue();

        return;
      }

      if (
        status === "expired"
      ) {
        toast.error(
          res.data.message
        );

        toast.clearWaitingQueue();

        logout();
      }
    };

  const onFormSubmit =
    async (formValues) => {
      const documentsValid =
        validateDocumentsBeforeSubmit(
          formValues
        );

      if (!documentsValid) {
        return;
      }

      setIsLoading(true);

      try {
        /*
         * This FormData contains the text
         * fields that will be encrypted.
         */
        const payload =
          new FormData();

        payload.append(
          "heading",
          formValues.heading.trim()
        );

        payload.append(
          "hn_heading",
          formValues.heading_hindi.trim()
        );

        payload.append(
          "title",
          formValues.title.trim()
        );

        payload.append(
          "hn_title",
          formValues.title_hindi.trim()
        );

        payload.append(
          "date",
          formatDateForApi(
            formValues.uploading_date
          )
        );

        payload.append(
          "doc_format",
          formValues
            .documentFormate.value
        );

        payload.append(
          "description",
          formValues.desc.trim()
        );

        payload.append(
          "hn_description",
          formValues.desc_hindi.trim()
        );

        payload.append(
          "status",
          String(
            formValues.status.value
          )
        );

        payload.append(
          "type",
          formValues.page_type.value
        );

        if (
          mode === "Edit"
        ) {
          if (!editingId) {
            throw new Error(
              "News ID is missing. Please reopen the record and try again."
            );
          }

          payload.append(
            "id",
            String(editingId)
          );
        }

        const encryptedData =
          await encryptFormData(
            payload
          );

        /*
         * This is the actual request body.
         *
         * Encrypted fields are sent inside
         * zero.
         *
         * Files remain unencrypted because
         * multer needs real File objects.
         */
        const requestData =
          new FormData();

        requestData.append(
          "zero",
          encryptedData
        );

        /*
         * ADD:
         * Both files are required and
         * appended here.
         *
         * EDIT:
         * File fields are omitted when the
         * user keeps the existing files.
         *
         * The backend must retain existing
         * file paths when these fields are
         * absent.
         */
        if (
          isFile(
            formValues.documentUpload
          )
        ) {
          requestData.append(
            "document",
            formValues.documentUpload
          );
        }

        if (
          isFile(
            formValues.documentUploadHN
          )
        ) {
          requestData.append(
            "hn_document",
            formValues.documentUploadHN
          );
        }

        const response =
          mode === "Add"
            ? await addNewsAPI(
                requestData
              )
            : await editNewsAPI(
                requestData
              );

        await handleApiResponse(
          response
        );
      } catch (error) {
        console.error(
          `Unable to ${mode.toLowerCase()} news:`,
          error
        );

        toast.error(
          error?.response?.data
            ?.message ||
            error?.message ||
            `Unable to ${mode.toLowerCase()} news.`
        );

        toast.clearWaitingQueue();
      } finally {
        setIsLoading(false);
      }
    };

  const toggleModal = () => {
    setModalOpen(
      (previous) => !previous
    );
  };

  const handleDocumentClick = (
    filePath,
    label
  ) => {
    const fileUrl =
      buildFileUrl(filePath);

    if (!fileUrl) {
      return;
    }

    const extension =
      getFileExtension(filePath);

    if (extension === "pdf") {
      setCurrentImageIndex(0);

      setTitleName(label);

      setFileType(extension);

      setModalImages([
        fileUrl,
      ]);

      setModalOpen(true);

      return;
    }

    window.open(
      fileUrl,
      "_blank",
      "noopener,noreferrer"
    );
  };

  const downloadExistingDocument =
    (filePath) => {
      const fileUrl =
        buildFileUrl(filePath);

      if (!fileUrl) {
        return;
      }

      const link =
        window.document.createElement(
          "a"
        );

      link.href = fileUrl;

      link.target = "_blank";

      link.rel =
        "noopener noreferrer";

      link.download =
        String(filePath)
          .split("/")
          .pop() ||
        "document";

      window.document.body.appendChild(
        link
      );

      link.click();

      window.document.body.removeChild(
        link
      );
    };

  const handleToggleStatus = (
    row
  ) => {
    setStatusRecord({
      id: row?.id,
      status: normalizeBoolean(
        row?.status
      ),
    });

    setShowModal(true);
  };

  const confirmStatusChange =
    async () => {
      if (!statusRecord?.id) {
        setShowModal(false);
        return;
      }

      try {
        const encryptedData =
          encrypt({
            id: statusRecord.id,
            status:
              statusRecord.status,
          });

        const res =
          await updateNewsStatusAPI({
            zero: encryptedData,
          });

        const status = decrypt(
          res.data.status
        );

        if (
          status === "success"
        ) {
          toast.success(
            res.data.message
          );

          toast.clearWaitingQueue();

          await getNEWSList();
        } else if (
          status === "failed"
        ) {
          toast.error(
            res.data.message
          );

          toast.clearWaitingQueue();
        } else if (
          status === "expired"
        ) {
          toast.error(
            res.data.message
          );

          toast.clearWaitingQueue();

          logout();
        }
      } catch (error) {
        console.error(
          "Error updating status:",
          error
        );

        toast.error(
          error?.response?.data
            ?.message ||
            error?.message ||
            "Unable to update status."
        );
      } finally {
        setShowModal(false);

        setStatusRecord(null);
      }
    };

  const columns = [
    {
      name: "Heading",

      selector: (row) =>
        row?.heading,

      sortable: true,

      wrap: true,

      cell: (row) => (
        <div>
          <span>
            {row?.heading}
          </span>

          <br />

          <span>
            {row?.hn_heading}
          </span>
        </div>
      ),
    },
    {
      name: "Title",

      selector: (row) =>
        row?.title,

      sortable: true,

      wrap: true,

      cell: (row) => (
        <div>
          <span>
            {row?.title}
          </span>

          <br />

          <span>
            {row?.hn_title}
          </span>
        </div>
      ),
    },
    {
      name: "Date",

      selector: (row) =>
        row?.date,

      sortable: true,

      wrap: true,
    },
    {
      name: "Document",

      selector: (row) =>
        row?.document,

      wrap: true,

      cell: (row) => {
        const englishExtension =
          getFileExtension(
            row?.document
          );

        const hindiExtension =
          getFileExtension(
            row?.hn_document
          );

        return (
          <div className="upload-image-div my-2">
            {row?.document && (
              <Button
                outline
                id={`doc-en-${row.id}`}
                color="danger"
                type="button"
                onClick={() =>
                  handleDocumentClick(
                    row.document,
                    "English Document"
                  )
                }
                size="xxl"
                title="View English Document"
                className="mb-2"
              >
                {englishExtension ===
                "pdf" ? (
                  <FaFilePdf
                    style={{
                      height: "1rem",
                      width: "1rem",
                    }}
                  />
                ) : (
                  <BsFiletypeDocx
                    style={{
                      height: "1rem",
                      width: "1rem",
                    }}
                  />
                )}{" "}
                English
              </Button>
            )}

            {row?.hn_document && (
              <Button
                outline
                id={`doc-hn-${row.id}`}
                color="danger"
                type="button"
                onClick={() =>
                  handleDocumentClick(
                    row.hn_document,
                    "Hindi Document"
                  )
                }
                size="xxl"
                title="View Hindi Document"
              >
                {hindiExtension ===
                "pdf" ? (
                  <FaFilePdf
                    style={{
                      height: "1rem",
                      width: "1rem",
                    }}
                  />
                ) : (
                  <BsFiletypeDocx
                    style={{
                      height: "1rem",
                      width: "1rem",
                    }}
                  />
                )}{" "}
                Hindi
              </Button>
            )}
          </div>
        );
      },
    },
    {
      name:
        "Document Format",

      selector: (row) =>
        row?.doc_format,

      sortable: true,

      wrap: true,
    },
    {
      name:
        "Description (EN)",

      selector: (row) =>
        row?.description,

      sortable: true,

      wrap: true,
    },
    {
      name:
        "Description (HN)",

      selector: (row) =>
        row?.hn_description,

      sortable: true,

      wrap: true,
    },
    {
      name: "Page Type",

      selector: (row) =>
        row?.type,

      sortable: true,

      wrap: true,
    },
    {
      name: "Status",

      selector: (row) =>
        normalizeBoolean(
          row?.status
        ),

      sortable: true,

      wrap: true,

      cell: (row) => {
        const active =
          normalizeBoolean(
            row?.status
          );

        return (
          <div
            style={{
              cursor: "pointer",
            }}
            onClick={() =>
              handleToggleStatus(
                row
              )
            }
          >
            <Badge
              color={
                active
                  ? "success"
                  : "danger"
              }
            >
              {active
                ? "Active"
                : "InActive"}
            </Badge>
          </div>
        );
      },
    },
    {
      name: "Action",

      wrap: true,

      cell: (row) => (
        <Button
          outline
          color="warning"
          size="xs"
          type="button"
          className="me-1"
          onClick={() =>
            handleEdit(row)
          }
        >
          <BiEdit />
        </Button>
      ),
    },
  ];

  return (
    <>
      <Head title="News" />

      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle
                page
                tag="h3"
              >
                News/ Events and
                Announcements
              </BlockTitle>
            </BlockHeadContent>

            <BlockHeadContent>
              <Button
                color="primary"
                outline
                type="button"
                className="me-n1"
                onClick={
                  handleAddToggle
                }
              >
                <Icon
                  name={
                    isOpen &&
                    (mode === "Add"||mode=="Edit")
                      ? "minus"
                      : "plus"
                  }
                />
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Row className="g-gs">
            {isOpen && (
              <PreviewAltCard>
                <Form
                  onSubmit={handleSubmit(
                    onFormSubmit
                  )}
                  noValidate
                >
                  <Row>
                    <Col
                      md="4"
                      sm="6"
                      xxl="3"
                    >
                      <FormGroup className="mb-2">
                        <Label
                          className="form-label"
                          htmlFor="page_type"
                        >
                          Page Type
                        </Label>

                        <Controller
                          name="page_type"
                          control={
                            control
                          }
                          rules={{
                            required:
                              "Please select page type",
                          }}
                          render={({
                            field,
                          }) => (
                            <Select
                              {...field}
                              inputId="page_type"
                              options={
                                PAGE_TYPE_OPTIONS
                              }
                              isClearable
                            />
                          )}
                        />

                        {errors.page_type && (
                          <span
                            className="invalid"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .page_type
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  <div className="row form">
                    <div className="col-lg-3">
                      <FormGroup>
                        <Label htmlFor="heading">
                          Heading
                          (English)
                        </Label>

                        <input
                          id="heading"
                          placeholder="Enter Heading (English)"
                          type="text"
                          className="form-control"
                          {...register(
                            "heading",
                            {
                              required:
                                "Heading is required",
                            }
                          )}
                        />

                        {errors.heading && (
                          <span
                            className="invalid"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .heading
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </div>

                    <div className="col-lg-3">
                      <FormGroup>
                        <Label htmlFor="heading_hindi">
                          Heading
                          (Hindi)
                        </Label>

                        <input
                          id="heading_hindi"
                          placeholder="शीर्षक दर्ज करें (Hindi)"
                          type="text"
                          className="form-control"
                          {...register(
                            "heading_hindi",
                            {
                              required:
                                "शीर्षक आवश्यक है",

                              pattern: {
                                value:
                                  HINDI_TEXT_PATTERN,

                                message:
                                  "केवल हिंदी में दर्ज करें",
                              },
                            }
                          )}
                        />

                        {errors.heading_hindi && (
                          <span
                            className="error"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .heading_hindi
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </div>

                    <div className="col-lg-3">
                      <FormGroup>
                        <Label htmlFor="title">
                          Title
                          (English)
                        </Label>

                        <input
                          id="title"
                          placeholder="Enter Title (English)"
                          type="text"
                          className="form-control"
                          {...register(
                            "title",
                            {
                              required:
                                "Title is required",
                            }
                          )}
                        />

                        {errors.title && (
                          <span
                            className="invalid"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .title
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </div>

                    <div className="col-lg-3">
                      <FormGroup>
                        <Label htmlFor="title_hindi">
                          Title (Hindi)
                        </Label>

                        <input
                          id="title_hindi"
                          placeholder="शीर्षक दर्ज करें (Hindi)"
                          type="text"
                          className="form-control"
                          {...register(
                            "title_hindi",
                            {
                              required:
                                "शीर्षक आवश्यक है",

                              pattern: {
                                value:
                                  HINDI_TEXT_PATTERN,

                                message:
                                  "केवल हिंदी में दर्ज करें",
                              },
                            }
                          )}
                        />

                        {errors.title_hindi && (
                          <span
                            className="error"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .title_hindi
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row form">
                    <div className="col-lg-3">
                      <FormGroup>
                        <Label htmlFor="uploading_date">
                          Uploading
                          Date
                        </Label>

                        <br />

                        <Controller
                          name="uploading_date"
                          control={
                            control
                          }
                          rules={{
                            required:
                              "Date field is required",
                          }}
                          render={({
                            field,
                          }) => (
                            <DatePicker
                              id="uploading_date"
                              placeholderText="Please select date"
                              selected={
                                field.value
                              }
                              onChange={
                                field.onChange
                              }
                              onBlur={
                                field.onBlur
                              }
                              className="form-control"
                              dateFormat="dd/MM/yyyy"
                            />
                          )}
                        />

                        {errors.uploading_date && (
                          <span
                            className="error"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .uploading_date
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </div>

                    <div className="col-lg-3">
                      <FormGroup>
                        <Label htmlFor="documentFormate">
                          Document
                          Format
                        </Label>

                        <Controller
                          name="documentFormate"
                          control={
                            control
                          }
                          rules={{
                            required:
                              "Please select format",
                          }}
                          render={({
                            field,
                          }) => (
                            <Select
                              {...field}
                              inputId="documentFormate"
                              options={
                                DOCUMENT_FORMAT_OPTIONS
                              }
                              isClearable
                              onChange={(
                                selectedOption
                              ) =>
                                handleFormatChange(
                                  selectedOption,
                                  field.onChange
                                )
                              }
                            />
                          )}
                        />

                        {errors.documentFormate && (
                          <span
                            className="error"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .documentFormate
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </div>

                    <div className="col-lg-3">
                      <FormGroup>
                        <Label htmlFor="documentUpload">
                          Upload
                          Document in
                          English
                        </Label>

                        {showUploadField ? (
                          <>
                            <Input
                              id="documentUpload"
                              type="file"
                              name="documentUpload"
                              innerRef={
                                englishFileInputRef
                              }
                              disabled={
                                !selectedFormat
                              }
                              accept={
                                selectedFormat ===
                                "pdf"
                                  ? "application/pdf,.pdf"
                                  : selectedFormat ===
                                    "docs"
                                  ? ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                  : ""
                              }
                              onChange={(
                                event
                              ) =>
                                handleFileChange(
                                  event,
                                  "documentUpload"
                                )
                              }
                            />

                            {mode ===
                              "Edit" &&
                              document && (
                                <Button
                                  type="button"
                                  size="sm"
                                  color="light"
                                  className="mt-1"
                                  onClick={() => {
                                    setShowUploadField(
                                      false
                                    );

                                    setValue(
                                      "documentUpload",
                                      null
                                    );

                                    clearErrors(
                                      "documentUpload"
                                    );

                                    if (
                                      englishFileInputRef.current
                                    ) {
                                      englishFileInputRef.current.value =
                                        "";
                                    }
                                  }}
                                >
                                  Keep
                                  Existing
                                </Button>
                              )}
                          </>
                        ) : (
                          <div className="d-flex justify-content-between align-items-center gap-2">
                            <div className="text-truncate">
                              <strong>
                                Uploaded:
                              </strong>{" "}
                              <button
                                type="button"
                                className="btn btn-link p-0 align-baseline"
                                onClick={() =>
                                  downloadExistingDocument(
                                    document
                                  )
                                }
                              >
                                {document
                                  ?.split(
                                    "/"
                                  )
                                  .pop()}
                              </button>
                            </div>

                            <Button
                              type="button"
                              size="sm"
                              color="primary"
                              onClick={() => {
                                setShowUploadField(
                                  true
                                );

                                setValue(
                                  "documentUpload",
                                  null
                                );

                                clearErrors(
                                  "documentUpload"
                                );
                              }}
                            >
                              Change
                            </Button>
                          </div>
                        )}

                        {errors.documentUpload && (
                          <span
                            className="error-message"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .documentUpload
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </div>

                    <div className="col-lg-3">
                      <FormGroup>
                        <Label htmlFor="documentUploadHN">
                          Upload
                          Document in
                          Hindi
                        </Label>

                        {showUploadFieldHN ? (
                          <>
                            <Input
                              id="documentUploadHN"
                              type="file"
                              name="documentUploadHN"
                              innerRef={
                                hindiFileInputRef
                              }
                              disabled={
                                !selectedFormat
                              }
                              accept={
                                selectedFormat ===
                                "pdf"
                                  ? "application/pdf,.pdf"
                                  : selectedFormat ===
                                    "docs"
                                  ? ".doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                  : ""
                              }
                              onChange={(
                                event
                              ) =>
                                handleFileChange(
                                  event,
                                  "documentUploadHN"
                                )
                              }
                            />

                            {mode ===
                              "Edit" &&
                              documentHN && (
                                <Button
                                  type="button"
                                  size="sm"
                                  color="light"
                                  className="mt-1"
                                  onClick={() => {
                                    setShowUploadFieldHN(
                                      false
                                    );

                                    setValue(
                                      "documentUploadHN",
                                      null
                                    );

                                    clearErrors(
                                      "documentUploadHN"
                                    );

                                    if (
                                      hindiFileInputRef.current
                                    ) {
                                      hindiFileInputRef.current.value =
                                        "";
                                    }
                                  }}
                                >
                                  Keep
                                  Existing
                                </Button>
                              )}
                          </>
                        ) : (
                          <div className="d-flex justify-content-between align-items-center gap-2">
                            <div className="text-truncate">
                              <strong>
                                Uploaded:
                              </strong>{" "}
                              <button
                                type="button"
                                className="btn btn-link p-0 align-baseline"
                                onClick={() =>
                                  downloadExistingDocument(
                                    documentHN
                                  )
                                }
                              >
                                {documentHN
                                  ?.split(
                                    "/"
                                  )
                                  .pop()}
                              </button>
                            </div>

                            <Button
                              type="button"
                              size="sm"
                              color="primary"
                              onClick={() => {
                                setShowUploadFieldHN(
                                  true
                                );

                                setValue(
                                  "documentUploadHN",
                                  null
                                );

                                clearErrors(
                                  "documentUploadHN"
                                );
                              }}
                            >
                              Change
                            </Button>
                          </div>
                        )}

                        {errors.documentUploadHN && (
                          <span
                            className="error-message"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .documentUploadHN
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </div>
                  </div>

                  <div className="row form">
                    <div className="col-md-12">
                      <FormGroup>
                        <Label htmlFor="desc">
                          Description
                          (English)
                        </Label>

                        <textarea
                          id="desc"
                          placeholder="Enter Description (English)"
                          className="form-control"
                          {...register(
                            "desc",
                            {
                              required:
                                "Description is required",
                            }
                          )}
                        />

                        {errors.desc && (
                          <span
                            className="invalid"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .desc
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </div>

                    <div className="col-md-12">
                      <FormGroup>
                        <Label htmlFor="desc_hindi">
                          Description
                          (Hindi)
                        </Label>

                        <textarea
                          id="desc_hindi"
                          placeholder="विवरण दर्ज करें (Hindi)"
                          className="form-control"
                          {...register(
                            "desc_hindi",
                            {
                              required:
                                "विवरण आवश्यक है",

                              pattern: {
                                value:
                                  HINDI_TEXT_PATTERN,

                                message:
                                  "केवल हिंदी में दर्ज करें",
                              },
                            }
                          )}
                        />

                        {errors.desc_hindi && (
                          <span
                            className="error"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .desc_hindi
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </div>

                    <div className="col-lg-3">
                      <FormGroup>
                        <Label
                          className="form-label"
                          htmlFor="status"
                        >
                          Status
                        </Label>

                        <Controller
                          name="status"
                          control={
                            control
                          }
                          rules={{
                            required:
                              "Please select status",
                          }}
                          render={({
                            field,
                          }) => (
                            <Select
                              {...field}
                              inputId="status"
                              options={
                                STATUS_OPTIONS
                              }
                              isClearable
                            />
                          )}
                        />

                        {errors.status && (
                          <span
                            className="invalid"
                            style={
                              ERROR_STYLE
                            }
                          >
                            {
                              errors
                                .status
                                .message
                            }
                          </span>
                        )}
                      </FormGroup>
                    </div>
                  </div>

                  <Col md="2">
                    <div
                      className="form-group"
                      style={{
                        marginTop:
                          "31px",
                      }}
                    >
                      <Button
                        disabled={
                          isLoading
                        }
                        color="primary"
                        size="md"
                        type="submit"
                      >
                        {isLoading ? (
                          <Spinner
                            size="sm"
                            color="light"
                          />
                        ) : mode ===
                          "Add" ? (
                          "Save"
                        ) : (
                          "Update"
                        )}
                      </Button>
                    </div>
                  </Col>
                </Form>
              </PreviewAltCard>
            )}
          </Row>

          <MyDataTable
            data={data}
            columns={columns}
            title="News Data"
            isLoading={isLoading}
          />

          <ViewImgAndPdf
            fileType={fileType}
            titleName={titleName}
            isOpen={modalOpen}
            toggleModal={
              toggleModal
            }
            images={modalImages}
            currentImageIndex={
              currentImageIndex
            }
            setCurrentImageIndex={
              setCurrentImageIndex
            }
          />

          <ConfirmModal
            isOpen={showModal}
            toggle={() => {
              setShowModal(
                (previous) =>
                  !previous
              );

              setStatusRecord(null);
            }}
            onConfirm={
              confirmStatusChange
            }
            status={
              statusRecord?.status
            }
          />
        </Block>
      </Content>
    </>
  );
};

export default News;